import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {TextDialog} from "../dialogs";
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  Button,
  Card,
  CardHeader,
  Collapse,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Typography
} from '@material-ui/core';
import { DraggableDialog, PromptDialog } from '../dialogs';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {ipcRenderer} from '../electron-modules';
import { useSnackbar } from 'notistack';
import {changeSwitched} from "../redux/settings-slice";
import { textToColor } from '../viewer/js/labelling/categories';
import { ArrowTooltip } from '../tooltips';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleClose: {
      position: 'absolute',
      right: theme.spacing(0),
      color: theme.palette.grey[500],
    },
    titleInfo: {
      position: 'absolute',
      right: theme.spacing(4),
      marginTop: theme.spacing(0),
      color: theme.palette.grey[500],
    },
    titleMin: {
      position: 'absolute',
      right: theme.spacing(4),
      marginTop: theme.spacing(-1),
      color: theme.palette.grey[500],
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    box: {
      padding: theme.spacing(4)
    },
    formControl: {
      width: '100%'
    },
    categoryName: {
      flex: 1
    },
    success: {
      color: theme.palette.success.main
    },
    warning: {
      color: theme.palette.warning.main
    },
    error: {
      color: theme.palette.error.main
    },
    info: {
      color: theme.palette.info.main
    },
    folderList: {
      padding: "0px",
      overflow: "auto",
      flex: 1,
      alignItems: "center",
      justifyContent: "center"
    },
    headerTitle: {
      fontWeight: "bold",
      cursor: "pointer"
    },
    cardHeader: {
      padding: "10px"
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    labelTitle: {
      flex: 1,
      userSelect: "none"
    },
    card: {
      margin: '10px',
      userSelect: "none"
    },
  }),
);

const Label = (props) => {
  const {viewer, label, index} = props;

  const classes = useStyles();
  const selected = label.selected;

  const selectLabel = (id) => {
    viewer?.selectLabel(id);
  };

  const completeLabel = (id) => {
    viewer?.completeLabel(id);
  };

  const deleteLabel = (id) => {
    viewer?.deleteLabel(id);
  };

  return (
    <ListItem selected={selected}>

      <Typography variant="caption" className={classes.labelTitle}>
        {`Label #${index + 1} (${label.numPoints})`}
      </Typography>

      {/* Complete Label */}
      {selected && (<React.Fragment>
        <ArrowTooltip title="Complete Label">
          <IconButton
            size="small"
            aria-label="complete"
            onClick={() => completeLabel(label.id)}
          >
            <CheckIcon
              fontSize="small"
              className={classes.success}
            />
          </IconButton>
        </ArrowTooltip>
      </React.Fragment>)}

      {/* Drawing Info */}
      {(!selected) && (<React.Fragment>
        <ArrowTooltip title="Click on points to begin editing polygon">
          <IconButton
            size="small"
            aria-label="complete"
          >
            <InfoIcon
              fontSize="small"
            />
          </IconButton>
        </ArrowTooltip>
      </React.Fragment>)}

      {/* Select Label */}
      <ArrowTooltip title="Select Label">
        <IconButton
          size="small"
          aria-label="close"
          onClick={() => selectLabel(label.id)}
        >
          <EditIcon fontSize="small"/>
        </IconButton>
      </ArrowTooltip>

      {/* Delete Label */}
      <ArrowTooltip title="Delete Label">
        <IconButton
          size="small"
          aria-label="close"
          onClick={() => deleteLabel(label.id)}
        >
          <CloseIcon fontSize="small" className={classes.error}/>
        </IconButton>
      </ArrowTooltip>

    </ListItem>
  );
};

const Category = (props) => {
  const {viewer, category, isValidCategory} = props;

  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [editPrompt, setEditPrompt] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);

  const formatName = (name) => {
    return (name.length > 12) ? name.substr(0,11) + "..." : name;
  };

  const addThenExpand = (categoryID) => {
    addLabel(categoryID);
    setExpanded(true);
  };

  const addLabel = (id) => {
    viewer?.addLabel(id);
  };

  const handleDeleteCategory = () => {
    viewer?.deleteCategory(category.id);
    setDeletePrompt(false);
  };

  const handleEditCategory = (name) => {
    if (!isValidCategory(name)) {
      return;
    }

    viewer?.editCategoryName(category.id, name);
    setEditPrompt(false);
  };

  const color = textToColor(category.name);

  return (
    <React.Fragment>
      <Card className={classes.card}>
        {/* Header */}
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/* Expand/collapse measurement */}
              <ArrowTooltip title={expanded? "Collapse" : "Expand"}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  {expanded && <ExpandLessIcon fontSize="small"/>}
                  {!expanded && <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
              </ArrowTooltip>

              {/* Edit category name */}
              <ArrowTooltip title="Edit Name">
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={() => {
                    setEditPrompt(true);
                  }}
                >
                  <EditIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/* Add new label */}
              <ArrowTooltip title="Add new label">
                <IconButton
                  size="small"
                  aria-label="add"
                  onClick={() => addThenExpand(category.id)}
                >
                  <AddToPhotosIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/* Delete category */}
              <ArrowTooltip title="Delete category">
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={() => setDeletePrompt(true)}
                >
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

            </React.Fragment>
          }
          disableTypography={true}
          title={
            <Typography
              variant="body2"
              style={{color}}
              className={classes.headerTitle}
            >
              {formatName(category.name)}
            </Typography>
          }
          subheader={
            <Typography
              variant="caption"
            >
              {`Total Labels: ${category.labels.length}`}
            </Typography>
          }
        />

        {/* Expandable content */}
        <Collapse in={expanded}>
          <List className={classes.folderList}>
            {category.labels.map((label, index) =>
              <Label
                key={label.id}
                viewer={viewer}
                label={label}
                index={index}
              />
            )}
          </List>
        </Collapse>

      </Card>

      <TextDialog
        open={editPrompt}
        onCancel={() => setEditPrompt(false)}
        onSubmit={handleEditCategory}
        title="Edit Category"
        prompt="Enter new category name"
        default={category.name}
        label="Category Name"
        clear={true}
      />

      <PromptDialog
        open={deletePrompt}
        onCancel={() => setDeletePrompt(false)}
        onSubmit={handleDeleteCategory}
        title="Delete Category?"
        prompt={`Do you want to delete this category (${category.name})? All labels will be removed`}
        button="Delete"
      />
    </React.Fragment>
  );
};

export const ImageLabelling = (props) => {
  const {viewer, setDefaultTab, setTabsEnabled, categoryList,
    imageLabelOpen, setImageLabelOpen} = props;

  const classes = useStyles();
  const showMessage = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();

  const [labelType, setLabelType] = useState(null);
  const [minimized, setMinimized] = useState(null);
  const [addCategory, setAddCategory] = useState(false);
  const [closePrompt, setClosePrompt] = useState(false);
  const [modalName, setModalName] = useState("");

  const isOrthoLabel = labelType === "ortho";
  const isSceneLabel = labelType === "scene";

  const handleOpen = (type) => {
    setImageLabelOpen(true);
    setLabelType(type);
  };

  const handleClose = () => {
    setImageLabelOpen(false);
  };

  const handleMinimize = () => {
    setMinimized(true);
  };

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  const isValidCategory = (name) => {
    // Uniqueness check
    const isUnique = viewer?.checkCategoryUniqueness(name);
    if (!isUnique) {
      showMessage(`Category already exists`, {
        variant: "error"
      });
      return false;
    }

    // Alphanumeric, dash and underscore
    const alphanumeric = /^[a-zA-Z0-9-_]+$/;
    if (!alphanumeric.test(name)) {
      showMessage(`Invalid category name. Allowed characters are: [a-z, 0-9, _, -]`, {
        variant: "error"
      });
      return false;
    }

    return true;
  };

  const handleCancel = () => {
    setAddCategory(false);
  };

  const handleAddCategory = (name) => {
    if (!isValidCategory(name)) {
      return;
    }

    viewer?.addCategory(name);
    setAddCategory(false);
  };

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-label-ortho");
    ipcRenderer?.on("open-label-ortho", () => {
      handleOpen("ortho");
    });

    ipcRenderer?.removeAllListeners("open-label-scene");
    ipcRenderer?.on("open-label-scene", () => {
      handleOpen("scene");
    });
  }, [viewer, imageLabelOpen, labelType]);

  useEffect(() => {
    viewer?.setActiveLabelType(labelType);

    if (isOrthoLabel) {
      dispatch(changeSwitched(true));
      setModalName("Orthomosaic Labelling");
    } else if (isSceneLabel) {
      dispatch(changeSwitched(false));
      setModalName("Scene-View Labelling");
    }
  }, [labelType]);

  useEffect(() => {
    if (imageLabelOpen) {
      setDefaultTab();
    } else {
      if (!minimized) {
        setLabelType(null);
      }
      setMinimized(false);
    }

    setTabsEnabled(!imageLabelOpen);
  }, [viewer, imageLabelOpen]);

  useEffect(() => {
    if (minimized) {
      setImageLabelOpen(false);
    }
  }, [minimized]);

  const isModified = categoryList.length !== 0;

  return (
    <React.Fragment>
      <DraggableDialog
        narrow
        open={imageLabelOpen}
        title={modalName}
        onClose={checkClose}
        onMinimize={handleMinimize}
      >
        <DialogContent>
          <List className={classes.formControl}>
            {categoryList.map(category =>
              <Category
                key={category.id}
                viewer={viewer}
                category={category}
                isValidCategory={isValidCategory}
              />)}
          </List>
        </DialogContent>

        <DialogActions>
          <Button color="primary">
            Export to Training
          </Button>

          <Button
            color="primary"
            onClick={() => setAddCategory(true)}
          >
            Add Category
          </Button>
        </DialogActions>

      </DraggableDialog>

      <TextDialog
        open={addCategory}
        onCancel={handleCancel}
        onSubmit={handleAddCategory}
        title="Create New Category"
        prompt="Enter new category name"
        default=''
        label="Category Name"
        clear={true}
      />

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          handleClose();
          setClosePrompt(false);
        }}
        title="Close Labelling?"
        prompt={`Do you want to close the ${modalName} window? All created labels will be lost.`}
        button="Close"
      />
    </React.Fragment>
  );
};